import React from 'react';

// The statement panel: the site's repeating motif, used for answers, docs callouts and security facts.
export function Statement({ title, meta, footer, className = '', shadow = true, children }) {
  return (
    <div className={`statement ${shadow ? 'shadow-panel' : 'border-x border-b border-stone'} ${className}`}>
      {(title || meta) && (
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 px-5 pb-3 pt-4 sm:px-7">
          {title && <span className="label text-wall">{title}</span>}
          {meta && <span className="text-[13px] text-glass figure">{meta}</span>}
        </div>
      )}
      {children}
      {footer && <div className="border-t border-stone px-5 py-3 text-[13px] text-glass sm:px-7">{footer}</div>}
    </div>
  );
}

export function StatementRow({ label, value, note, strong = false, valueClass = '' }) {
  return (
    <div className={`statement-row px-5 sm:px-7 ${strong ? 'border-t-wall/70 font-semibold' : ''}`}>
      <span className={strong ? '' : 'text-glass'}>{label}</span>
      <span className={`figure whitespace-nowrap text-right ${valueClass}`}>
        {value}
        {note && <span className="ml-3 font-normal text-glass">{note}</span>}
      </span>
    </div>
  );
}

const SQL_WORDS =
  /\b(SELECT|FROM|WHERE|JOIN|ON|AND|AS|BETWEEN|GROUP BY|ORDER BY|DESC|LIMIT|USING|SUM)\b|('[^']*')/g;

// Highlights only keywords and string literals. There is deliberately no other colour in the code.
function highlightSql(code) {
  const parts = [];
  let last = 0;
  code.replace(SQL_WORDS, (match, word, str, index) => {
    if (index > last) parts.push(code.slice(last, index));
    parts.push(
      <span key={index} className={str ? 'text-glass-light' : 'text-bronze-light'}>
        {match}
      </span>
    );
    last = index + match.length;
    return match;
  });
  if (last < code.length) parts.push(code.slice(last));
  return parts;
}

// Dark code panel. It scrolls inside itself and can be reached by keyboard to do so.
export function CodePanel({ title, meta, footer, code, language, className = '', maxHeight }) {
  return (
    <figure className={`on-dark m-0 flex min-w-0 flex-col bg-wall text-limestone ${className}`}>
      {(title || meta) && (
        <figcaption className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-limestone/15 px-5 py-3 text-[13px] sm:px-6">
          {title && <span className="font-semibold">{title}</span>}
          {meta && <span className="text-glass-light figure">{meta}</span>}
        </figcaption>
      )}
      <pre
        tabIndex={0}
        aria-label={title ? `${title}, scrollable code` : 'Scrollable code'}
        className="code-text m-0 flex-1 overflow-auto px-5 py-5 sm:px-6"
        style={maxHeight ? { maxHeight } : undefined}
      >
        <code>{language === 'sql' ? highlightSql(code) : code}</code>
      </pre>
      {footer && (
        <div className="flex flex-wrap justify-between gap-x-6 gap-y-1 border-t border-limestone/15 px-5 py-3 text-[13px] text-glass-light figure sm:px-6">
          {footer}
        </div>
      )}
    </figure>
  );
}
